import React, { useCallback, useEffect, useImperativeHandle, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import queryString from 'query-string';

import PaymentInfo from './PaymentInfo';
import { AdditionalPaymentAction, GetPaymentMethods } from 'common/types';

import * as ShopSelectors from 'selectors/ShopSelectors';
import { adyenAdditionalDetails, adyenCardAuth, AdyenPaymentResult } from './adyenUtils';
import { useLocation } from 'react-router-dom';
import { newFirestoreId } from 'common/utils/newRentalUtils';
import { fetchAdyenPaymentMethods } from 'actions/ShopActions';
import errorHandler from 'common/services/errorHandler';
import { useTranslation } from 'services/localization';
import { useAdyen } from 'common/hooks/useAdyen';
import { AdyenPaymentMethod } from 'common/modules/payments/types';
import { PaymentSubmit } from './AddCardDialog';
import { AdditionalDetailsUpdate } from 'common/components/payment/PaymentMethodSelector';
import { lang as langSelector } from 'selectors/LangSelector';
import { AdyenProvider } from 'common/hooks/useAdyen/AdyenContext';
import { BILLING_CURRENCY } from 'common/modules/billing';

interface Props {
	setDialogOpen: (val: boolean) => void;
}

const AdyenCardForm = React.forwardRef<PaymentSubmit, Props>((props, payBtnRef) => {
	const { search } = useLocation();
	const adyen = useAdyen();
	const dispatch = useDispatch();
	const { t } = useTranslation();
	const adyenPaymentMethods = useSelector(ShopSelectors.adyenPaymentMethods);
	const shopId = useSelector(ShopSelectors.activeShopId)!;
	const shopCountry = useSelector(ShopSelectors.activeShopCountry);
	const storedPaymentMethods = adyenPaymentMethods.data?.storedPaymentMethods;
	const [paymentError, setPaymentError] = useState<null | string>(null);
	const [paymentLoading, setPaymentLoading] = useState<boolean>(false);
	const [paymentInProgress, setPaymentInProgress] = useState<boolean>(false);
	const [additionalPaymentAction, setAdditionalPaymentAction] = useState<AdditionalPaymentAction>(
		undefined,
	);
	const { transactionId: queryTransactionId } = queryString.parse(search);
	const hasQueryTransactionId = Boolean(
		queryTransactionId && typeof queryTransactionId === 'string',
	);
	const [transactionId, setTransactionId] = useState<string>(
		hasQueryTransactionId ? (queryTransactionId as string) : newFirestoreId(),
	);
	const [initRedirectAction, setInitRedirectAction] = useState<boolean>(hasQueryTransactionId);

	useImperativeHandle(payBtnRef, () => ({
		submit: (method: AdyenPaymentMethod) => {
			handleCardConfirm(method);
		},
	}));

	const getAdyenPaymentMethods = useCallback(() => {
		const requestData: GetPaymentMethods = {
			shopId: shopId ?? '',
			amount: {
				value: 0,
				currency: BILLING_CURRENCY.code,
			},
			...(shopCountry && { countryCode: shopCountry.toUpperCase() }),
			shopperReference: shopId,
		};
		dispatch(fetchAdyenPaymentMethods(requestData));
	}, [dispatch, shopCountry, shopId]);

	useEffect(() => {
		if (shopId && !adyenPaymentMethods.data) {
			getAdyenPaymentMethods();
		}
	}, [dispatch, shopId, adyenPaymentMethods.data, getAdyenPaymentMethods]);

	useEffect(() => {
		if (initRedirectAction) {
			setInitRedirectAction(false);
			setPaymentInProgress(true);
			handleAdyenAdditionalDetailsUpdate({ provider: 'ADYEN', data: { redirect: true } });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [initRedirectAction]);

	const handleAdyenResult = (result: AdyenPaymentResult) => {
		setPaymentLoading(false);
		if (!!result.action) {
			setAdditionalPaymentAction({ provider: 'ADYEN', action: result.action });
			return;
		}
		setPaymentInProgress(false);
		setAdditionalPaymentAction(undefined);
		if (result.success) {
			getAdyenPaymentMethods();
			props.setDialogOpen?.(false);
			return;
		}
		setPaymentError(result.error || 'Error');
	};

	const handleCardConfirm = async (method: AdyenPaymentMethod) => {
		const paymentState = await adyen.submit(method);
		if (!paymentState || !paymentState.isValid) {
			return;
		}
		setPaymentError(null);
		setPaymentInProgress(true);
		setPaymentLoading(true);
		try {
			const result = await adyenCardAuth({
				shopId,
				adyenPaymentData: paymentState.data,
				primary: !storedPaymentMethods || storedPaymentMethods?.length === 0,
			});
			setTransactionId(result.transactionId);
			handleAdyenResult(result);
		} catch (e) {
			errorHandler.report(e);
			handlePaymentError();
		}
	};

	const handleAdyenAdditionalDetailsUpdate = async (
		detailsState: AdditionalDetailsUpdate | { provider: 'ADYEN'; data: { redirect: true } },
	) => {
		if (detailsState.provider !== 'ADYEN') return;
		setPaymentLoading(true);
		try {
			const result = await adyenAdditionalDetails({
				shopId,
				additionalDetailsData: detailsState.data,
				transactionId,
				primary: storedPaymentMethods?.length === 0,
			});
			handleAdyenResult(result);
		} catch (e) {
			errorHandler.report(e);
			handlePaymentError();
		}
	};

	const handlePaymentError = () => {
		const error = t('common:errors.error');
		setPaymentError(error);
		setPaymentLoading(false);
		setAdditionalPaymentAction(undefined);
		setPaymentInProgress(false);
	};

	return (
		<PaymentInfo
			onAdyenAdditionalDetails={handleAdyenAdditionalDetailsUpdate}
			paymentLoading={paymentLoading}
			paymentInProgress={paymentInProgress}
			additionalPaymentAction={additionalPaymentAction}
			paymentError={paymentError}
		/>
	);
});

const AdyenCardFormWrapper = React.forwardRef<PaymentSubmit, Props>((props, payBtnRef) => {
	const adyenPaymentMethods = useSelector(ShopSelectors.adyenPaymentMethods);
	const lang = useSelector(langSelector);

	return (
		<AdyenProvider paymentMethods={adyenPaymentMethods.data ?? null} lang={lang}>
			<AdyenCardForm ref={payBtnRef} setDialogOpen={props.setDialogOpen} />
		</AdyenProvider>
	);
});

export default AdyenCardFormWrapper;
